import React, { useState, useEffect } from 'react';
import { Card, Table, Button, DatePicker, Select, message, Spin, Row, Col, Statistic, Tag, Space, Radio, Divider } from 'antd';
import { ReloadOutlined, CalendarOutlined, BarChartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { formatIndianNumber } from '../../../utils/numberFormat';
import { getApiEndpoint } from '../../../config/apiBase';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;
const { RangePicker } = DatePicker;

const MonthlyDailyCount = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState('all'); // 'all' means all users
  const [users, setUsers] = useState([{ id: 'all', username: 'All Users' }]);
  const [summaryStats, setSummaryStats] = useState({});
  
  // Date filter state
  const [dateMode, setDateMode] = useState('daily'); // 'daily' or 'monthly'
  const [dailyRange, setDailyRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [selectedMonths, setSelectedMonths] = useState([dayjs().month()]);
  const [filterValid, setFilterValid] = useState(true);

  // Generate year options (2023 to current year)
  const generateYearOptions = () => {
    const currentYear = dayjs().year();
    const years = [];
    // Only show last 3 years + current year to prevent confusion
    for (let year = Math.max(2023, currentYear - 3); year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };

  // Month options
  const monthOptions = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
  ];

  // Quick presets for daily mode
  const quickPresets = [
    { label: 'Today', range: [dayjs(), dayjs()] },
    { label: 'Yesterday', range: [dayjs().subtract(1, 'days'), dayjs().subtract(1, 'days')] },
    { label: 'Last 7 Days', range: [dayjs().subtract(6, 'days'), dayjs()] },
    { label: 'Last 30 Days', range: [dayjs().subtract(29, 'days'), dayjs()] },
  ];

  // Load filter state from sessionStorage
  useEffect(() => {
    // Clear any corrupted sessionStorage data
    const savedFilter = sessionStorage.getItem('monthlyDailyCountFilter');
    if (savedFilter) {
      try {
        const filter = JSON.parse(savedFilter);
        // Check if filter has corrupted data
        if (filter.mode === 'daily' && (!filter.from_date || !filter.to_date)) {
          console.log('[SessionStorage] Clearing corrupted filter data');
          sessionStorage.removeItem('monthlyDailyCountFilter');
          setDailyRange([dayjs().subtract(30, 'days'), dayjs()]);
          return;
        }
        console.log('[SessionStorage] Loading filter:', filter);
        setDateMode(filter.mode || 'daily');
        if (filter.mode === 'daily' && filter.from_date && filter.to_date) {
          // Convert string dates back to moment objects
          const newRange = [dayjs(filter.from_date), dayjs(filter.to_date)];
          console.log('[SessionStorage] Converting to moment objects:', {
            from: filter.from_date,
            to: filter.to_date,
            newRange: newRange.map(d => ({ isMoment: dayjs.isDayjs(d), value: d }))
          });
          setDailyRange(newRange);
        } else if (filter.mode === 'monthly') {
          // Validate year is not in future
          const currentYear = dayjs().year();
          const year = filter.year || dayjs().year();
          if (year > currentYear) {
            console.log('[SessionStorage] Invalid year from storage, using current year');
            setSelectedYear(currentYear);
          } else {
            setSelectedYear(year);
          }
          setSelectedMonths(filter.months || [dayjs().month()]);
        }
      } catch (e) {
        console.error('Failed to load saved filter:', e);
        // Reset to default if loading fails
        sessionStorage.removeItem('monthlyDailyCountFilter');
        setDateMode('daily');
        setDailyRange([dayjs().subtract(30, 'days'), dayjs()]);
      }
    } else {
      // No saved filter, ensure we have valid defaults
      console.log('[SessionStorage] No saved filter, using defaults');
      setDailyRange([dayjs().subtract(30, 'days'), dayjs()]);
    }
  }, []);

  // Save filter state to sessionStorage
  useEffect(() => {
    // Only save if we have valid data
    if (dateMode === 'daily' && (!dailyRange || !Array.isArray(dailyRange) || dailyRange.length !== 2)) {
      return;
    }
    
    const filter = {
      mode: dateMode,
      ...(dateMode === 'daily' 
        ? { 
            from_date: dailyRange && dailyRange[0] && dayjs.isDayjs(dailyRange[0]) 
              ? dailyRange[0].format('YYYY-MM-DD') 
              : null, 
            to_date: dailyRange && dailyRange[1] && dayjs.isDayjs(dailyRange[1]) 
              ? dailyRange[1].format('YYYY-MM-DD') 
              : null 
          }
        : { year: selectedYear, months: selectedMonths }
      )
    };
    sessionStorage.setItem('monthlyDailyCountFilter', JSON.stringify(filter));
  }, [dateMode, dailyRange, selectedYear, selectedMonths]);

  // Fetch users for dropdown
  useEffect(() => {
    fetchUsers();
  }, []);

  // Note: Removed auto-fetch on filter change since we now have a Submit button

  // Validate date range
  useEffect(() => {
    if (dateMode === 'daily') {
      // Check if dailyRange is valid before destructuring
      if (!dailyRange || !Array.isArray(dailyRange) || dailyRange.length !== 2) {
        console.log('[Validation] Invalid dailyRange array');
        setFilterValid(false);
        return;
      }
      const [start, end] = dailyRange;
      
      // First check if they are moment objects
      const isStartValid = start && dayjs.isDayjs(start);
      const isEndValid = end && dayjs.isDayjs(end);
      
      if (!isStartValid || !isEndValid) {
        console.log('[Validation] Dates are not moment objects', { 
          start: typeof start, 
          end: typeof end,
          startValue: start,
          endValue: end
        });
        setFilterValid(false);
        return;
      }
      
      // Now safe to call moment methods
      const isEndAfterStart = end.isSameOrAfter(start);
      const isEndNotFuture = end.isSameOrBefore(dayjs());
      const isRangeValid = start.diff(end, 'days') >= -31;
      
      console.log('[Validation] Debug:', {
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
        today: dayjs().format('YYYY-MM-DD'),
        isStartValid,
        isEndValid,
        isEndAfterStart,
        isEndNotFuture,
        isRangeValid,
        daysDiff: start.diff(end, 'days')
      });
      
      const isValid = isStartValid && isEndValid && isEndAfterStart && isEndNotFuture && isRangeValid;
      setFilterValid(isValid);
    } else {
      const isValid = selectedYear && selectedMonths.length > 0 && selectedMonths.length <= 12;
      setFilterValid(isValid);
    }
  }, [dateMode, dailyRange, selectedYear, selectedMonths]);

  const fetchUsers = async () => {
    try {
      // Get unique users from migrated_sms_summary
      const response = await fetch(getApiEndpoint('/migration/sms-summary/users'));
      if (response.ok) {
        const result = await response.json();
        // Add "All Users" option at the beginning
        const allUsersOption = { user_id: 'all', user_name: 'All Users' };
        // Transform the data structure to match what the component expects
        const transformedUsers = [allUsersOption, ...(result.data || [])].map(user => ({
          id: user.user_id,
          username: user.user_name
        }));
        setUsers(transformedUsers);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      // Fallback to mock users with All Users option
      setUsers([
        { id: 'all', username: 'All Users' },
        { id: 9, username: 'nimich' },
        { id: 10, username: 'user2' },
        { id: 11, username: 'user3' },
      ]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build backend-compatible payload
      let payload = {};
      
      if (dateMode === 'daily') {
        // Check if dailyRange exists and is valid
        if (!dailyRange || !Array.isArray(dailyRange) || dailyRange.length !== 2) {
          console.error('[MonthlyDailyCount] Invalid dailyRange');
          message.error('Please select a valid date range');
          setLoading(false);
          return;
        }
        const [start, end] = dailyRange;
        // Check if start and end are valid moment objects
        if (!start || !end || !dayjs.isDayjs(start) || !dayjs.isDayjs(end)) {
          console.error('[MonthlyDailyCount] Invalid moment objects in dailyRange');
          message.error('Please select a valid date range');
          setLoading(false);
          return;
        }
        payload = {
          start_date: start.format('YYYY-MM-DD'),
          end_date: end.format('YYYY-MM-DD')
        };
      } else {
        // For monthly mode, use the first selected month's range
        if (selectedMonths.length > 0) {
          const firstMonth = Math.min(...selectedMonths);
          const lastMonth = Math.max(...selectedMonths);
          
          const start = dayjs().year(selectedYear).month(firstMonth).date(1);
          const end = dayjs().year(selectedYear).month(lastMonth).date(1).endOf('month');
          
          payload = {
            start_date: start.format('YYYY-MM-DD'),
            end_date: end.format('YYYY-MM-DD')
          };
        }
      }
      
      // Log payload for debugging
      console.log('[MonthlyDailyCount] API Payload:', payload);
      
      // Validate payload before sending
      if (!payload.start_date || !payload.end_date) {
        console.error('[MonthlyDailyCount] Invalid payload: missing dates');
        message.error('Invalid date selection');
        return;
      }
      
      // Build URL with correct parameters
      let url = getApiEndpoint('/migration/sms-summary/data') + '?';
      const params = new URLSearchParams();
      
      params.append('startDate', payload.start_date);
      params.append('endDate', payload.end_date);
      params.append('limit', '500');
      
      if (selectedUser && selectedUser !== 'all') {
        params.append('user_id', selectedUser);
      }
      
      url += params.toString();
      
      console.log('[MonthlyDailyCount] API URL:', url);
      
      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        console.log('[MonthlyDailyCount] API Response:', result);
        
        if (result.data && result.data.length > 0) {
          const groupedData = groupByDate(result.data);
          setData(groupedData);
          calculateSummaryStats(groupedData);
        } else {
          // Handle empty response
          setData([]);
          setSummaryStats({});
          message.info('No data found for selected date range');
        }
      } else {
        message.error('Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const groupByDate = (data) => {
    const grouped = {};
    
    data.forEach(item => {
      const date = dayjs(item.report_date).format('YYYY-MM-DD');
      if (!grouped[date]) {
        grouped[date] = {
          date: date,
          displayDate: dayjs(item.report_date).format('DD MMM YYYY'),
          sms_unit: 0,
          sms_count: 0,
          sent_count: 0,
          delivered_count: 0,
          failed_count: 0,
          pending_count: 0,
          blocked_count: 0,
          not_delivered_count: 0,
          dr_not_available_count: 0,
          others_count: 0,
          recordCount: 0,
        };
      }
      
      // Sum up all metrics for the day
      grouped[date].sms_unit += item.sms_unit || 0;
      grouped[date].sms_count += item.sms_count || 0;
      grouped[date].sent_count += item.sent_count || 0;
      grouped[date].delivered_count += item.delivered_count || 0;
      grouped[date].failed_count += item.failed_count || 0;
      grouped[date].pending_count += item.pending_count || 0;
      grouped[date].blocked_count += item.blocked_count || 0;
      grouped[date].not_delivered_count += item.not_delivered_count || 0;
      grouped[date].dr_not_available_count += item.dr_not_available_count || 0;
      grouped[date].others_count += item.others_count || 0;
      grouped[date].recordCount += 1;
    });
    
    // Convert to array and sort by date
    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  };

  const calculateSummaryStats = (data) => {
    if (!data || data.length === 0) {
      setSummaryStats({});
      return;
    }

    const stats = data.reduce((acc, item) => {
      acc.totalSent += item.sent_count || 0;
      acc.totalDelivered += item.delivered_count || 0;
      acc.totalFailed += item.failed_count || 0;
      acc.totalPending += item.pending_count || 0;
      acc.totalBlocked += item.blocked_count || 0;
      acc.totalDays += 1;
      
      return acc;
    }, {
      totalSent: 0,
      totalDelivered: 0,
      totalFailed: 0,
      totalPending: 0,
      totalBlocked: 0,
      totalDays: 0,
    });

    // Calculate averages and rates with proper rounding
    stats.avgSMSPerDay = stats.totalDays > 0 ? (stats.totalSent / stats.totalDays).toFixed(2) : "0.00";
    stats.deliveryRate = stats.totalSent > 0 ? ((stats.totalDelivered / stats.totalSent) * 100).toFixed(2) : "0.00";
    
    setSummaryStats(stats);
  };

  const exportToCSV = () => {
    const headers = [
      'Date', 'SMS Unit', 'SMS Count', 'Sent', 'Delivered', 
      'Failed', 'Pending', 'Blocked', 'Not Delivered', 'DR Not Available', 'Others'
    ];
    
    const csvContent = [
      headers.join(','),
      ...data.map(item => [
        item.date,
        item.sms_unit,
        item.sms_count,
        item.sent_count,
        item.delivered_count,
        item.failed_count,
        item.pending_count,
        item.blocked_count,
        item.not_delivered_count,
        item.dr_not_available_count,
        item.others_count
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily_sms_count_${dateMode}_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Get filter summary text
  const getFilterSummary = () => {
    if (dateMode === 'daily') {
      // Check if dailyRange exists and is an array
      if (!dailyRange || !Array.isArray(dailyRange) || dailyRange.length !== 2) {
        return 'Daily Data: Select date range';
      }
      const [start, end] = dailyRange;
      // Check if start and end are valid moment objects
      if (!start || !end || !dayjs.isDayjs(start) || !dayjs.isDayjs(end)) {
        return 'Daily Data: Select date range';
      }
      if (start.isSame(end)) {
        return `Daily Data: ${start.format('DD MMM YYYY')}`;
      }
      return `Daily Data: ${start.format('DD MMM YYYY')} → ${end.format('DD MMM YYYY')}`;
    } else {
      const monthNames = selectedMonths.map(m => monthOptions[m].label.slice(0, 3));
      return `Monthly Data: ${monthNames.join(', ')} ${selectedYear}`;
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (_, record) => (
        <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
          {record.displayDate}
        </span>
      ),
      width: 140,
      sorter: (a, b) => a.date.localeCompare(b.date),
    },
    {
      title: 'SMS Unit',
      dataIndex: 'sms_unit',
      key: 'sms_unit',
      render: (value) => formatIndianNumber(value),
      align: 'right',
      sorter: (a, b) => a.sms_unit - b.sms_unit,
    },
    {
      title: 'SMS Count',
      dataIndex: 'sms_count',
      key: 'sms_count',
      render: (value, record) => (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
            {formatIndianNumber(value)}
          </div>
        </div>
      ),
      align: 'right',
      sorter: (a, b) => a.sms_count - b.sms_count,
    },
    {
      title: 'Sent',
      dataIndex: 'sent_count',
      key: 'sent_count',
      render: (value) => formatIndianNumber(value),
      align: 'right',
      sorter: (a, b) => a.sent_count - b.sent_count,
    },
    {
      title: 'Delivered',
      dataIndex: 'delivered_count',
      key: 'delivered_count',
      render: (value) => formatIndianNumber(value),
      align: 'right',
      sorter: (a, b) => a.delivered_count - b.delivered_count,
    },
    {
      title: 'Failed',
      dataIndex: 'failed_count',
      key: 'failed_count',
      render: (value) => (
        <span style={{ color: value > 0 ? '#ff4d4f' : 'inherit' }}>
          {formatIndianNumber(value)}
        </span>
      ),
      align: 'right',
      sorter: (a, b) => a.failed_count - b.failed_count,
    },
    {
      title: 'Pending',
      dataIndex: 'pending_count',
      key: 'pending_count',
      render: (value) => formatIndianNumber(value),
      align: 'right',
      sorter: (a, b) => a.pending_count - b.pending_count,
    },
    {
      title: 'Blocked',
      dataIndex: 'blocked_count',
      key: 'blocked_count',
      render: (value) => formatIndianNumber(value),
      align: 'right',
    },
    {
      title: 'Not Delivered',
      dataIndex: 'not_delivered_count',
      key: 'not_delivered_count',
      render: (value) => formatIndianNumber(value),
      align: 'right',
    },
    {
      title: 'DR Not Available',
      dataIndex: 'dr_not_available_count',
      key: 'dr_not_available_count',
      render: (value) => formatIndianNumber(value),
      align: 'right',
    },
    {
      title: 'Others',
      dataIndex: 'others_count',
      key: 'others_count',
      render: (value) => formatIndianNumber(value),
      align: 'right',
    },
    {
      title: 'Success Rate',
      key: 'success_rate',
      render: (_, record) => {
        const rate = record.sent_count > 0 
          ? ((record.delivered_count / record.sent_count) * 100).toFixed(2)
          : 0;
        const color = rate >= 95 ? '#52c41a' : rate >= 85 ? '#faad14' : '#ff4d4f';
        return (
          <div style={{ textAlign: 'right' }}>
            <span style={{ color }}>{rate}%</span>
            {rate >= 95 && <Tag color="green" size="small">Excellent</Tag>}
            {rate >= 85 && rate < 95 && <Tag color="gold" size="small">Good</Tag>}
            {rate < 85 && rate > 0 && <Tag color="red" size="small">Poor</Tag>}
          </div>
        );
      },
      align: 'right',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <span>
            Monthly/Daily SMS Count Report
            <Tag color="blue" style={{ marginLeft: 10 }}>
              {selectedUser && selectedUser !== 'all' ? (users.find(u => u.id === selectedUser)?.username || `User ${selectedUser}`) : 'All Users'}
            </Tag>
            <Tag color="green" style={{ marginLeft: 5 }}>
              {getFilterSummary()}
            </Tag>
          </span>
        }
        extra={
          <Space>
            {/* Date Mode Selector */}
            <Radio.Group 
              value={dateMode} 
              onChange={(e) => setDateMode(e.target.value)}
              size="small"
            >
              <Radio.Button value="daily">Daily</Radio.Button>
              <Radio.Button value="monthly">Monthly</Radio.Button>
            </Radio.Group>
            
            <Divider type="vertical" />
            
            {/* User Filter */}
            <Select
              style={{ width: 150 }}
              value={selectedUser}
              onChange={(value) => setSelectedUser(value)}
              placeholder="All Users"
              allowClear={false}
            >
              {users
                .filter(user => user.id !== null && user.id !== undefined)
                .map(user => (
                  <Option key={`user-${user.id}`} value={user.id}>
                    {user.username}
                  </Option>
                ))}
            </Select>
            
            {/* Date Filters */}
            {dateMode === 'daily' ? (
              <Space>
                <RangePicker
                  value={dailyRange}
                  onChange={setDailyRange}
                  format="YYYY-MM-DD"
                  disabledDate={(current) => {
                    if (!current) return false;
                    // Disable dates after today
                    return current && current > dayjs().endOf('day');
                  }}
                  onCalendarChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      const daysDiff = dates[1].diff(dates[0], 'days');
                      if (daysDiff > 31) {
                        message.warning('Maximum range is 31 days');
                        setDailyRange([dates[0], dates[0].clone().add(31, 'days')]);
                      }
                    }
                  }}
                />
                <Select
                  style={{ width: 120 }}
                  placeholder="Quick Preset"
                  onChange={(value) => {
                    const preset = quickPresets.find(p => p.label === value);
                    if (preset) {
                      setDailyRange(preset.range);
                    }
                  }}
                  allowClear
                >
                  {quickPresets.map(preset => (
                    <Option key={preset.label} value={preset.label}>
                      {preset.label}
                    </Option>
                  ))}
                </Select>
              </Space>
            ) : (
              <Space>
                <Select
                  style={{ width: 100 }}
                  value={selectedYear}
                  onChange={(value) => {
                    // Ensure year doesn't exceed current year
                    const currentYear = dayjs().year();
                    if (value > currentYear) {
                      message.warning(`Year cannot be greater than ${currentYear}`);
                      return;
                    }
                    setSelectedYear(value);
                  }}
                >
                  {generateYearOptions().map(year => (
                    <Option key={year} value={year}>{year}</Option>
                  ))}
                </Select>
                <Select
                  mode="multiple"
                  style={{ width: 300 }}
                  placeholder="Select months"
                  value={selectedMonths}
                  onChange={setSelectedMonths}
                  maxTagCount={3}
                >
                  {monthOptions.map(month => (
                    <Option key={month.value} value={month.value}>
                      {month.label}
                    </Option>
                  ))}
                </Select>
                <Button 
                  size="small"
                  onClick={() => setSelectedMonths([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11])}
                  disabled={selectedMonths.length === 12}
                >
                  Select All
                </Button>
                {selectedMonths.length > 12 && (
                  <span style={{ color: '#ff4d4f' }}>Max 12 months</span>
                )}
              </Space>
            )}
            
            <Button 
              type="primary"
              loading={loading}
              onClick={fetchData}
              disabled={!filterValid}
            >
              Submit
            </Button>
            
            <Button 
              onClick={() => {
                // Clear all filters and reset to defaults
                sessionStorage.removeItem('monthlyDailyCountFilter');
                setDateMode('daily');
                setDailyRange([dayjs().subtract(30, 'days'), dayjs()]);
                setSelectedYear(dayjs().year());
                setSelectedMonths([dayjs().month()]);
                setSelectedUser('all');
                setData([]);
                setSummaryStats({});
              }}
            >
              Clear Filters
            </Button>
            
            <Button 
              onClick={exportToCSV}
              disabled={data.length === 0}
            >
              Export CSV
            </Button>
          </Space>
        }
      >
        {/* Validation Message */}
        {!filterValid && (
          <div style={{ marginBottom: 16, padding: 12, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4 }}>
            <span style={{ color: '#ff4d4f' }}>
              {dateMode === 'daily' 
                ? 'Please select a valid date range (max 31 days, not in future)'
                : 'Please select at least 1 and at most 12 months'
              }
            </span>
          </div>
        )}
        
        {/* Summary Statistics */}
        {Object.keys(summaryStats).length > 0 && (
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={4}>
              <Statistic
                title="Total Days"
                value={summaryStats.totalDays}
                styles={{ content: { color: '#1890ff' } }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Sent Count"
                value={summaryStats.totalSent}
                formatter={(value) => formatIndianNumber(value)}
                styles={{ content: { color: '#1890ff' } }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Avg SMS/Day"
                value={summaryStats.avgSMSPerDay}
                formatter={(value) => formatIndianNumber(value)}
                styles={{ content: { color: '#722ed1' } }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Delivery Rate"
                value={summaryStats.deliveryRate}
                suffix="%"
                styles={{ 
                  content: {
                    color: summaryStats.deliveryRate >= 95 ? '#52c41a' : 
                           summaryStats.deliveryRate >= 85 ? '#faad14' : '#ff4d4f' 
                  }
                }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Total Failed"
                value={summaryStats.totalFailed}
                formatter={(value) => formatIndianNumber(value)}
                styles={{ content: { color: '#ff4d4f' } }}
              />
            </Col>
          </Row>
        )}

        {/* Data Table */}
        {!loading && data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
            <BarChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div style={{ fontSize: 16, marginBottom: 8 }}>No data found</div>
            <div style={{ fontSize: 14 }}>Try adjusting your date filter or check if data exists for the selected period</div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={Array.isArray(data) ? data : []}
            rowKey="date"
            loading={loading}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} days`,
              defaultPageSize: 31,
            }}
            scroll={{ x: 1400 }}
            summary={(pageData) => {
            let totalSent = 0;
            let totalDelivered = 0;
            let totalFailed = 0;
            let totalPending = 0;
            
            pageData.forEach(({ 
              sent_count, delivered_count, 
              failed_count, pending_count 
            }) => {
              totalSent += sent_count || 0;
              totalDelivered += delivered_count || 0;
              totalFailed += failed_count || 0;
              totalPending += pending_count || 0;
            });
            
            const overallRate = totalSent > 0 ? ((totalDelivered / totalSent) * 100).toFixed(2) : "0.00";
            
            return (
              <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                <Table.Summary.Cell index={0}>
                  <strong>TOTAL ({pageData.length} days)</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>-</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <strong style={{ fontSize: '16px' }}>{formatIndianNumber(totalSent)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <strong>{formatIndianNumber(totalSent)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <strong>{formatIndianNumber(totalDelivered)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <strong style={{ color: totalFailed > 0 ? '#ff4d4f' : 'inherit' }}>
                    {formatIndianNumber(totalFailed)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <strong>{formatIndianNumber(totalPending)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  <strong>-</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">
                  <strong>-</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="right">
                  <strong>-</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10} align="right">
                  <strong>-</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11} align="right">
                  <strong>{overallRate}%</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
        )}
      </Card>
    </div>
  );
};

export default MonthlyDailyCount;
